import { generateErrors } from 'utilities/util';
import { ThunkDispatch } from 'redux-thunk';
import { Action } from 'redux';
import { TYPES } from 'action-types/mdm';
import { callApiV3Get, callSettingsApiGet} from 'utilities/apiCaller';
import API from 'constants/api';
import { TransportMode } from 'constants/modes';
import { IStore } from 'reducers/index';

export const checkVendor = (code: { beCode?: string; factCode?: string }, uniqueId?: string) => {
	const queryParam = code.beCode
    ? `beCode=${encodeURIComponent(code.beCode)}`
    : code.factCode
    ? `factCode=${encodeURIComponent(code.factCode)}`
    : '';
	const url = `${API.MDM.CheckCode}?${queryParam}`;

	return (dispatch: ThunkDispatch<any, any, Action>, getState: () => IStore) => {
		dispatch({ type: TYPES.FETCH_MDM_CODE.PENDING });
		
		return new Promise((resolve, reject) => {
			callApiV3Get(url, {}, { headers: { TransportMode: TransportMode.Ocean } })
				.then((res) => {
					const mdmObj = res.data
					if(uniqueId){
						mdmObj.uniqueId = uniqueId
					}
					dispatch({ type: TYPES.FETCH_MDM_CODE.FULFILLED, mdm: mdmObj });
					resolve(mdmObj);
				})
				.catch((e:any) => {
					const errors = generateErrors(e);
					dispatch({ type: TYPES.FETCH_MDM_CODE.REJECTED, errors });
					reject(errors);
				});
		});
	};
};

export const getPrinters = (countryId:string) => {
    return async (dispatch: ThunkDispatch<any, any, Action>) => {
        dispatch({ type: TYPES.FETCH_PRINTER.PENDING });
        try {
            const res = await mapPrinterDto(countryId);
            dispatch({ type: TYPES.FETCH_PRINTER.FULFILLED, printers: res });
        } catch (e) {
            const errors = generateErrors(e);
            dispatch({ type: TYPES.FETCH_PRINTER.REJECTED, errors });
        }
    }
};
export const getOutputs = (countryId:string) => {
    return async (dispatch: ThunkDispatch<any, any, Action>) => {
        dispatch({ type: TYPES.FETCH_OUTPUT.PENDING });
        try {
            const res = await mapInvoiceOutputTypesDto(countryId);
            dispatch({ type: TYPES.FETCH_OUTPUT.FULFILLED, outputs: res });
        } catch (e) {
            const errors = generateErrors(e);
            dispatch({ type: TYPES.FETCH_OUTPUT.REJECTED, errors });
        }
    }
};
export const getReasonCodes = (category: string) => {
    return async (dispatch: ThunkDispatch<any, any, Action>) => {
        dispatch({ type: TYPES.FETCH_REASON_CODES_OUTPUT.PENDING });
        try {
            const res = await mapReasonCodesDto(category);
            dispatch({ type: TYPES.FETCH_REASON_CODES_OUTPUT.FULFILLED, reasonCodes: res, category });
            
        } catch (e) {
            const errors = generateErrors(e);
            dispatch({ type: TYPES.FETCH_REASON_CODES_OUTPUT.REJECTED, errors });
        }
    }
};

interface IPrinter {
	readonly id: number;
	readonly code: string;
	readonly description: string;
	readonly country: string;
}

interface ISettingsPrinter {
	readonly printerIdentifier: number;
	readonly printerCode: string;
	readonly printerDescription: string;
	readonly countryCode: string;
}

async function mapPrinterDto(countryId?: string) {
    const countryCodes = [countryId, '*'];
    const page = 1;
    const limit = 1000;

    const promises = countryCodes.map((countryCode) => 
        callSettingsApiGet(`${API.FinanceConfig.Printers}`, {countryCode, page, limit}) 
    );

    const results = await Promise.all(promises);

    return [].concat.apply([], results.map((result) => result.data.data))
        .map((printer: ISettingsPrinter) => <IPrinter>{
            id: printer.printerIdentifier,
            code: printer.printerCode,
            description: printer.printerDescription,
            country: printer.countryCode
        })
        .sort((a: IPrinter, b: IPrinter) => {
            if (a.code < b.code) {
                return -1;
            } 
            if (a.code > b.code) {
                return 1;
            }
            return 0;
        })
}

interface IInvoiceOutputType {
	readonly id: number;
	readonly code: string;
	readonly name: string;
}

interface ISettingsInvoiceOutputType {
	readonly invoiceOutputTypeIdentifier: number;
	readonly invoiceOutputTypeCode: string;
	readonly invoiceOutputTypeName: string;
	readonly countryCode: string;
}

async function mapInvoiceOutputTypesDto(countryId?: string) {
    const countryCodes = [countryId, '*'];
    const page = 1;
    const limit = 1000;

    const promises = countryCodes.map((countryCode) => 
        callSettingsApiGet(`${API.FinanceConfig.InvoiceOutputTypes}`, {countryCode, page, limit}) 
    );

    const results = await Promise.all(promises);

    return [].concat.apply([], results.map((result) => result.data.data))
        .map((invoiceOutputType: ISettingsInvoiceOutputType) => <IInvoiceOutputType>{
            id: invoiceOutputType.invoiceOutputTypeIdentifier,
            code: invoiceOutputType.invoiceOutputTypeCode,
            name: invoiceOutputType.invoiceOutputTypeName
        })
        .sort((a: IInvoiceOutputType, b: IInvoiceOutputType) => {
            if (a.id < b.id) {
                return -1;
            } 
            if (a.id > b.id) {
                return 1;
            }
            return 0;
        })
}

interface IReasonCode {
	readonly id: number;
	readonly code: string;
	readonly description: string;
	readonly category: string;
}

interface ISettingsReasonCode {
	readonly reasonCodeIdentifier: number;
	readonly reasonCodeNumber: string;
	readonly reasonDescription: string;
	readonly reasonCodeCategory: string;
}

async function mapReasonCodesDto(reasonCodeCategory?: string) {
    const page = 1;
    const limit = 1000;

    const reasonCodesForCategory = await callSettingsApiGet(`${API.FinanceConfig.ReasonCodes}`, {reasonCodeCategory, page, limit});

    return reasonCodesForCategory.data.data
        .map((reasonCode: ISettingsReasonCode) => <IReasonCode>{
            id: reasonCode.reasonCodeIdentifier,
            code: reasonCode.reasonCodeNumber,
            description: reasonCode.reasonDescription,
            category: reasonCode.reasonCodeCategory
        })
        .sort((a: IReasonCode, b: IReasonCode) => {
            if (a.id < b.id) {
                return -1;
            } 
            if (a.id > b.id) {
                return 1;
            }
            return 0;
        })
}
